// Step 5 — one column's properties (prototype `propsCol`): General / Data /
// Display / Behaviour / Rules, built from the transaction designer's own
// editors (designerKit) so a column here is set up exactly as in the
// designer. A STANDARD column (shipped by the application) only unlocks what
// its `tenant_may` lists; once PUBLISHED a column's name, type, encryption and
// stored value codes are fixed ("retire it and add a new column").

import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { Copy, Database, Lock, Palette, Pencil, Plus, Power, Trash2, Undo2 } from 'lucide-react'
import {
  BusinessLogicFields, DisplayFormatEditor, EditMaskEditor, ItemChangeRuleEditor, LookupEditor,
  MASKS_WITH_VALUES, ValidationRuleEditor, type LookupPatch,
} from '../../designerKit'
import { useKitConfirm } from '../../designerKit/KitModal'
import { tw } from '../../testHooks'
import type { ColumnDef, EditMask, ItemChange, ItemChangeRule, Validation } from '../../txn/types'
import {
  isValidationRule, itemChangeSummary, normalizeItemChange, validationSummary,
} from '../../txn/types'
import { CALENDAR_TOKEN_LABELS, CALENDAR_TOKENS } from '../../txn/utils'
import type { UdaColumn } from '../types'
import {
  asColumnDef, canEdit, clone, COLUMN_TENANT_MAY_KEYS, editableLeaves, formOf, humanize, isStandard,
  KEY_RE, ORIGIN_TENANT, PUBLISHED_FIXED_LEAVES, storedSlot, takenKeys, truthy, typeOf,
} from './model'
import { opCol, opSet, useSetup, type ColTab } from './state'
import { HintBox, OriginPills, Seg, StateBox } from './ui'
import TenantMayChips from './TenantMayChips'

const TABS: Array<[ColTab, string]> = [
  ['general', 'General'], ['data', 'Data'], ['display', 'Display'], ['behaviour', 'Behaviour'], ['rules', 'Rules'],
]

/** Plain names for the designer keys a standard column may unlock. */
const LEAF_LABELS: Record<string, string> = {
  heading: 'heading', list_heading: 'list heading', descr: 'description', default_value: 'default value',
  protect: 'protected', mandatory: 'mandatory', min_length: 'min length', db_size: 'size', hidden: 'hidden',
  format: 'input pattern', text_case: 'text case', hide_label: 'caption', edit_mask: 'values', lookup: 'lookup',
  item_change: 'when it changes', validations: 'validations', display: 'display', pictures: 'pictures',
  width: 'width',
}

// The editors' look on this panel (the shared input rules: 36px, strong border).
const kitLabel: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 5px' }
const kitInput: CSSProperties = {
  width: '100%', minHeight: 36, padding: '7px 10px', borderRadius: 6, boxSizing: 'border-box',
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontSize: 14,
}
const kitAdd: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 13, marginTop: 4,
  color: 'var(--color-accent)', background: 'none', border: '1px dashed var(--color-border)', borderRadius: 4, cursor: 'pointer',
}

export default function ColumnProperties() {
  const ctx = useSetup()
  const { forms, formNo, draft, node, tab, setTab, mode, go, catalog, notify, project, openDialog } = ctx
  const form = formOf(draft.layer, formNo)
  const setup = forms.find(f => f.formNo === formNo) || null
  const parent = forms.find(f => f.formNo === setup?.parentFormNo) || null
  const setKey = node.t === 'col' ? node.set : ''
  const colName = node.t === 'col' ? node.col : ''
  const set = form?.sets.find(s => s.key === setKey)
  const c = set?.columns.find(x => x.name === colName)
  const { confirm, confirmDialog } = useKitConfirm(catalog)
  const [editor, setEditor] = useState<null | 'lookup' | 'rule' | 'display' | { validation: number }>(null)
  const [nameDraft, setNameDraft] = useState(colName)
  useEffect(() => { setNameDraft(colName) }, [colName])

  const pub = c ? draft.published.get(`${formNo}|${c.name}`) || null : null
  const leaves = useMemo(() => editableLeaves(mode, c, COLUMN_TENANT_MAY_KEYS), [mode, c])
  if (!form || !set || !c) return <div className="uds-pbody"><div className="uds-na">Pick a column.</div></div>
  const std = isStandard(c) && mode === 'tenant'
  const can = (leaf: string) => {
    if (mode === 'tenant' && pub && PUBLISHED_FIXED_LEAVES.includes(leaf)) return false
    if (leaf === 'name' || leaf === 'col_type' || leaf === 'secure' || leaf === 'in_grid') return !std
    return canEdit(leaves, leaf)
  }
  const edit = (fn: (col: UdaColumn) => void) => draft.edit(opCol(formNo, set.key, c.name, col => fn(col)))
  const colDef = asColumnDef(c, set.label || set.key)

  // Names {form.X} / {header.X} tokens and pickers offer.
  const ownNames = (setup?.columns || []).map(x => String(x.name || '').toLowerCase()).filter(Boolean)
  const attrNames = form.sets.flatMap(s => (s.columns || []).map(x => `uda_${x.name}`))
  const headerNames = (parent?.columns || []).map(x => String(x.name || '').toLowerCase()).filter(Boolean)
  const headerAttrNames = parent ? (formOf(draft.layer, parent.formNo)?.sets || []).flatMap(s => (s.columns || []).map(x => `uda_${x.name}`)) : []
  // An organization's rules may only fill additional attributes (policy).
  const fillTargets = mode === 'tenant' ? attrNames : [...ownNames, ...attrNames]

  const ic = normalizeItemChange(c.item_change as ItemChange | string | null | undefined)
  const icMode = ic ? ic.mode : ''
  const validations = (c.validations || []) as Validation[]
  const t = typeOf(draft.columnTypes, c.col_type)
  const sizeKind = t?.size_kind || 'none'
  const mask = c.edit_mask || { mask: '', values: [] }
  const withValues = (MASKS_WITH_VALUES as readonly string[]).includes(mask.mask)
  const pictures = c.pictures?.by_value || null
  const record = setup?.isHeader ? 'order list' : 'line grid'

  const nameProblem = (() => {
    const v = nameDraft.trim()
    if (v === c.name) return ''
    if (!KEY_RE.test(v)) return 'Use lower-case letters, digits and _ (start with a letter).'
    if (takenKeys(form, setup).filter(k => k !== c.name).includes(v)) return 'Another column on this form already has this name.'
    return ''
  })()
  const commitName = () => {
    const v = nameDraft.trim()
    if (!v || v === c.name || nameProblem) return
    const old = c.name
    draft.edit(opCol(formNo, set.key, old, col => { col.name = v }))
    go({ t: 'col', set: set.key, col: v })
  }

  const duplicate = () => {
    const taken = takenKeys(form, setup)
    let name = `${c.name}_copy`
    let n = 2
    while (taken.includes(name)) name = `${c.name}_copy${n++}`
    const y = clone(c)
    y.name = name
    y.heading = `${c.heading || c.name} (copy)`
    y.origin = ORIGIN_TENANT
    delete y.tenant_may
    delete y.retired
    const after = c.name
    draft.edit(opSet(formNo, set.key, s => {
      const i = s.columns.findIndex(x => x.name === after)
      s.columns.splice(i < 0 ? s.columns.length : i + 1, 0, y)
    }))
    go({ t: 'col', set: set.key, col: name })
  }
  const remove = async () => {
    if (pub && mode === 'tenant') {
      if (!(await confirm({ title: `Retire ${c.heading || c.name}?`, message: 'It stops being asked. Values already saved stay readable, and it can be brought back.', confirmLabel: 'Retire' }))) return
      edit(col => { col.retired = true })
      return
    }
    if (!(await confirm({ title: `Delete ${c.heading || c.name}?`, message: 'The column has not been published, so nothing has been saved in it.', confirmLabel: 'Delete' }))) return
    const name = c.name
    draft.edit(opSet(formNo, set.key, s => { s.columns = s.columns.filter(x => x.name !== name) }))
    go({ t: 'set', set: set.key })
  }

  const setMask = (m: EditMask) => {
    if (mode === 'tenant' && pub && pub.codes.length) {
      const kept = new Set(m.values.map(v => String(v.data ?? '').trim()))
      const lost = pub.codes.filter(code => !kept.has(code))
      if (lost.length) {
        notify('info', `${lost.join(', ')} ${lost.length === 1 ? 'is' : 'are'} already saved on records, so ${lost.length === 1 ? 'it' : 'they'} cannot be changed or removed. Add a new value instead.`)
        return
      }
    }
    edit(col => { col.edit_mask = { ...m, ...(m.mask === (col.edit_mask?.mask || '') && col.edit_mask?.style ? { style: col.edit_mask.style } : {}) } })
  }
  const setPicture = (code: string, swatch: string) => edit(col => {
    const p = clone(col.pictures || {}) as NonNullable<UdaColumn['pictures']>
    p.by_value = { ...(p.by_value || {}), [code]: { ...(p.by_value?.[code] || {}), swatch } }
    col.pictures = p
  })
  const setItemChange = (v: ItemChange | null) => edit(col => { col.item_change = v as UdaColumn['item_change'] })

  const setupRows: Array<[string, string, boolean, ColTab]> = [
    ['Lookup', c.lookup || 'None', !!c.lookup, 'behaviour'],
    ['When it changes', ic ? itemChangeSummary(ic) : 'None', !!ic, 'behaviour'],
    ['Validations', validations.length ? `${validations.length} validation${validations.length > 1 ? 's' : ''}` : 'None', !!validations.length, 'rules'],
    ['Default value', c.default_value || 'None', !!c.default_value, 'behaviour'],
    ['In lists', c.display?.type ? humanize(String(c.display.type)) : 'Plain text', !!c.display, 'display'],
  ]
  const allowedText = leaves ? [...leaves].map(k => LEAF_LABELS[k] || k).join(', ') : ''

  let body: React.ReactNode
  if (tab === 'general') {
    body = (
      <>
        <label className="uds-lbl" htmlFor="uds-cname">Column name</label>
        <input id="uds-cname" className="uds-inp mono" value={nameDraft} disabled={!can('name')}
          onChange={e => setNameDraft(e.target.value)} onBlur={commitName}
          onKeyDown={e => { if (e.key === 'Enter') commitName() }} {...tw('field', 'uda-col-name')} />
        {nameProblem && <div className="uds-hint" style={{ color: 'var(--color-error)' }}>{nameProblem}</div>}
        <HintBox icon={<Database size={15} />}>
          Saved as <span className="uds-mono">uda_{c.name}</span> in the additional attributes store, not as a column
          of {setup?.table || 'the form’s table'}.{pub ? ' The name is fixed once values are saved.' : ''}
        </HintBox>
        <label className="uds-lbl" htmlFor="uds-chead">Heading</label>
        <input id="uds-chead" className="uds-inp" value={c.heading || ''} disabled={!can('heading')}
          onChange={e => { const v = e.target.value; edit(col => { col.heading = v }) }} {...tw('field', 'uda-col-heading')} />
        <label className="uds-lbl" htmlFor="uds-cdesc">Description</label>
        <textarea id="uds-cdesc" className="uds-inp" rows={2} value={c.descr || ''} disabled={!can('descr')}
          onChange={e => { const v = e.target.value; edit(col => { col.descr = v }) }} />
        <div className="uds-lbl">Field state</div>
        <div className="uds-grid2">
          {([['mandatory', 'Mandatory'], ['protect', 'Protected'], ['hidden', 'Hidden']] as const).map(([k, label]) => (
            <StateBox key={k} checked={truthy(c[k])} disabled={!can(k)} onChange={v => edit(col => { col[k] = v })}>{label}</StateBox>
          ))}
        </div>
        <div className="uds-lbl">Set up on this column</div>
        <div className="uds-setup">
          {setupRows.map(([title, sub, on, goTab]) => (
            <button key={title} type="button" onClick={() => setTab(goTab)}>
              <span className={`d ${on ? 'on' : ''}`} />
              <span style={{ minWidth: 0 }}><span className="t">{title}</span><span className="s">{sub}</span></span>
              <span className="go">{TABS.find(x => x[0] === goTab)?.[1]}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 12 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={duplicate}><Copy size={14} /> Duplicate</button>
          {!std && !c.retired && (
            <button type="button" className="btn btn-secondary btn-sm uds-danger" onClick={() => { void remove() }}>
              <Trash2 size={14} /> {pub && mode === 'tenant' ? 'Retire' : 'Delete'}
            </button>
          )}
          {!std && c.retired && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => edit(col => { delete col.retired })}>
              <Undo2 size={14} /> Bring back
            </button>
          )}
        </div>
        {mode === 'design' && (
          <TenantMayChips value={c.tenant_may || []} vocabulary={COLUMN_TENANT_MAY_KEYS} labels={LEAF_LABELS}
            onChange={v => edit(col => { col.tenant_may = v })} what="this column" />
        )}
      </>
    )
  } else if (tab === 'data') {
    const types = draft.columnTypes
    body = (
      <>
        <label className="uds-lbl" htmlFor="uds-ctype">Type</label>
        {types.length ? (
          <select id="uds-ctype" className="uds-inp" value={t?.name || String(c.col_type || '')} disabled={!can('col_type')}
            onChange={e => {
              const nt = typeOf(types, e.target.value)
              edit(col => {
                col.col_type = e.target.value
                if (!nt || nt.size_kind !== sizeKind) col.db_size = ''
              })
            }}>
            {!t && c.col_type && <option value={c.col_type}>{c.col_type}</option>}
            {types.map(x => <option key={x.name} value={x.name}>{x.name}</option>)}
          </select>
        ) : (
          <input className="uds-inp mono" value={c.col_type || ''} disabled={!can('col_type')}
            onChange={e => { const v = e.target.value.toUpperCase(); edit(col => { col.col_type = v }) }} />
        )}
        {!can('col_type') && pub && <div className="uds-hint">The type is fixed once values are saved. Add a new column and retire this one to change it.</div>}
        {sizeKind !== 'none' && (
          <>
            <label className="uds-lbl" htmlFor="uds-csize">Size</label>
            <input id="uds-csize" className="uds-inp mono" value={c.db_size || ''} disabled={!can('db_size')}
              placeholder={sizeKind === 'precision_scale' ? 'precision,scale e.g. 12,2' : 'length e.g. 40'}
              onChange={e => { const v = e.target.value; edit(col => { col.db_size = v }) }} />
          </>
        )}
        {sizeKind === 'length' && (
          <>
            <label className="uds-lbl" htmlFor="uds-cmin">Min length</label>
            <input id="uds-cmin" className="uds-inp" value={String(c.min_length ?? '')} disabled={!can('min_length')} placeholder="No minimum"
              onChange={e => { const v = e.target.value.trim(); edit(col => { if (v) col.min_length = v; else delete col.min_length }) }} />
          </>
        )}
        <label className="uds-lbl">Stored in</label>
        <div className="uds-inp uds-mono" style={{ display: 'flex', alignItems: 'center' }}>twasta_uda_values.{storedSlot(c, types)}</div>
        <div className="uds-hint">Keyed by {setup?.table || 'the form'}&apos;s primary key. Saved and deleted with the {setup?.isHeader ? 'record' : 'line'}.</div>
        <div style={{ marginTop: 14 }}>
          <StateBox checked={!!c.secure} disabled={!can('secure')} onChange={v => edit(col => { if (v) col.secure = {} as UdaColumn['secure']; else delete col.secure })}>
            Sensitive: encrypt at rest
          </StateBox>
        </div>
      </>
    )
  } else if (tab === 'display') {
    body = (
      <>
        <div style={can('edit_mask') ? undefined : { pointerEvents: 'none', opacity: 0.6 }} aria-disabled={!can('edit_mask')}>
          <EditMaskEditor value={mask} onChange={setMask} label="Input widget"
            labelStyle={kitLabel} inputStyle={kitInput} addBtnStyle={kitAdd} />
        </div>
        {(mask.mask === 'radio' || mask.mask === 'bool_check') && (
          <>
            <div className="uds-lbl">Style</div>
            <Seg value={mask.style || ''} disabled={!can('edit_mask')} ariaLabel="Style"
              onChange={v => edit(col => { col.edit_mask = { ...(col.edit_mask || mask), style: (v || undefined) as EditMask['style'] } })}
              options={mask.mask === 'radio'
                ? [{ value: '', label: 'Buttons' }, { value: 'chips', label: 'Chips' }]
                : [{ value: '', label: 'Checkbox' }, { value: 'toggle', label: 'Toggle' }]} />
          </>
        )}
        {withValues && mask.values.length > 0 && (
          <div className="uds-pcard">
            <div className="uds-pcard-h"><Palette size={14} /><span className="uds-grow">A picture per value</span></div>
            <div className="uds-pcard-b">
              {pictures ? mask.values.map(v => (
                <div key={v.data} className="uds-vrow">
                  <input type="color" value={pictures[v.data]?.swatch || '#888888'} disabled={!can('pictures')}
                    aria-label={`Picture colour for ${v.display || v.data}`}
                    style={{ width: 36, height: 32, border: '1px solid var(--color-border-strong)', borderRadius: 6, background: 'var(--color-input-bg)', padding: 2 }}
                    onChange={e => setPicture(v.data, e.target.value)} />
                  <span style={{ fontSize: 14 }}>{v.display || v.data}</span>
                  <span className="uds-mono" style={{ color: 'var(--color-text-faint)' }}>{v.data}</span>
                </div>
              )) : (
                <button type="button" className="btn btn-secondary btn-sm" disabled={!can('pictures')}
                  onClick={() => edit(col => { col.pictures = { ...(col.pictures || {}), by_value: {} } })}>
                  Add a picture per value
                </button>
              )}
              <div className="uds-hint">Shown as a swatch on the chips, in the grid and in the line&apos;s details.</div>
            </div>
          </div>
        )}
        <label className="uds-lbl" htmlFor="uds-ccase">Text case</label>
        <select id="uds-ccase" className="uds-inp" value={c.text_case || ''} disabled={!can('text_case')}
          onChange={e => { const v = e.target.value; edit(col => { if (v) col.text_case = v as ColumnDef['text_case']; else delete col.text_case }) }}>
          <option value="">As typed</option><option value="upper">UPPER CASE</option><option value="lower">lower case</option>
        </select>
        <label className="uds-lbl" htmlFor="uds-cfmt">Input pattern</label>
        <input id="uds-cfmt" className="uds-inp mono" value={c.format || ''} disabled={!can('format')} placeholder="e.g. #,##0.00, LC-######, [Email]"
          onChange={e => { const v = e.target.value; edit(col => { col.format = v }) }} />
        <div style={{ marginTop: 12 }}>
          <StateBox checked={truthy(c.hide_label)} disabled={!can('hide_label')} onChange={v => edit(col => { col.hide_label = v })}>Hide the caption</StateBox>
        </div>
        <div className="uds-pcard">
          <div className="uds-pcard-h">In lists and grids</div>
          <div className="uds-pcard-b">
            <label className="uds-lbl">Display format</label>
            <div className="uds-vrow" style={{ marginTop: 0 }}>
              <div className="uds-inp" style={{ display: 'flex', alignItems: 'center' }}>{c.display?.type ? humanize(String(c.display.type)) : 'Plain text'}</div>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!can('display')} onClick={() => setEditor('display')}><Pencil size={14} /> Change</button>
            </div>
            <label className="uds-lbl" htmlFor="uds-clh">Heading in lists</label>
            <input id="uds-clh" className="uds-inp" value={c.list_heading || ''} disabled={!can('list_heading')} placeholder={c.heading || ''}
              onChange={e => { const v = e.target.value; edit(col => { col.list_heading = v }) }} />
            <div style={{ marginTop: 10 }}>
              <StateBox checked={truthy(c.in_grid)} disabled={!can('in_grid')} onChange={v => edit(col => { col.in_grid = v })}>
                Show as a column in the {record}
              </StateBox>
            </div>
            {truthy(c.in_grid) && (
              <>
                <label className="uds-lbl" htmlFor="uds-cw">Width</label>
                <input id="uds-cw" className="uds-inp" value={String(c.width ?? '') === '0' ? '' : String(c.width ?? '')} disabled={!can('width')} placeholder="Auto"
                  onChange={e => { const v = e.target.value.trim(); edit(col => { if (v) col.width = Number(v) || 0; else delete col.width }) }} />
              </>
            )}
            <div className="uds-hint">Records this column does not apply to show n/a.</div>
          </div>
        </div>
      </>
    )
  } else if (tab === 'behaviour') {
    const dateLike = ['date', 'datetime'].includes(t?.category || '')
    body = (
      <>
        <label className="uds-lbl" htmlFor="uds-cdef">Default value</label>
        <div className="uds-vrow" style={{ marginTop: 0 }}>
          <input id="uds-cdef" className="uds-inp" value={c.default_value || ''} disabled={!can('default_value')}
            placeholder={dateLike ? ':calendar_today or a literal' : 'A literal value'}
            onChange={e => { const v = e.target.value; edit(col => { col.default_value = v }) }} />
          {dateLike && (
            <select className="uds-inp" style={{ width: 44, padding: '0 4px' }} value="" disabled={!can('default_value')} aria-label="Pick a token"
              onChange={e => { const v = e.target.value; if (v) edit(col => { col.default_value = v }) }}>
              <option value="">Pick a token…</option>
              {CALENDAR_TOKENS.map(tk => <option key={tk} value={tk}>{CALENDAR_TOKEN_LABELS[tk]}</option>)}
            </select>
          )}
        </div>
        <div className="uds-pcard">
          <div className="uds-pcard-h"><span className="uds-grow">Lookup</span></div>
          <div className="uds-pcard-b">
            <div className="uds-vrow" style={{ marginTop: 0 }}>
              <div className="uds-inp uds-mono" style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>{c.lookup || c.lookup_sql ? (c.lookup || 'SQL lookup') : 'None'}</div>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!can('lookup')} onClick={() => setEditor('lookup')}><Pencil size={14} /> {c.lookup ? 'Change' : 'Set up'}</button>
            </div>
            <div className="uds-hint">For example PORT(PORT_NAME, PORT_CODE). The user picks a value from the table.</div>
          </div>
        </div>
        <div className="uds-pcard">
          <div className="uds-pcard-h"><span className="uds-grow">When it changes</span></div>
          <div className="uds-pcard-b">
            <Seg value={icMode as string} disabled={!can('item_change')} ariaLabel="When it changes"
              onChange={v => {
                if (!v) setItemChange(null)
                else if (v === 'rule') setEditor('rule')
                else if (v === 'business_logic') setItemChange({ mode: 'business_logic', business_logic: { method: `${c.name}_changed`, description: '', language: 'python' } } as ItemChange)
                else if (v === 'rest_api') setItemChange({ mode: 'rest_api', rest_api: { url: '', method: 'POST' } } as ItemChange)
              }}
              options={[{ value: '', label: 'None' }, { value: 'rule', label: 'Rule' }, { value: 'business_logic', label: 'Business logic' }, { value: 'rest_api', label: 'Call API' }]} />
            {ic?.mode === 'rule' && (
              <div style={{ marginTop: 10 }}>
                <div className="uds-li" style={{ marginTop: 0 }}><span className="t">{itemChangeSummary(ic)}</span>
                  <button type="button" className="uds-icbtn" aria-label="Edit the rule" disabled={!can('item_change')} onClick={() => setEditor('rule')}><Pencil size={14} /></button>
                </div>
              </div>
            )}
            {ic?.mode === 'business_logic' && (
              <>
                <BusinessLogicFields value={ic.business_logic}
                  onChange={bl => setItemChange({ mode: 'business_logic', business_logic: bl } as ItemChange)}
                  onOpenCode={() => openDialog({ kind: 'code', fnName: ic.business_logic.method || `${c.name}_changed`, title: c.heading || c.name })} />
                <div className="uds-hint">Receives the header, this form and its additional attributes. It may change additional attributes only.</div>
              </>
            )}
            {ic?.mode === 'rest_api' && (
              <>
                <label className="uds-lbl" htmlFor="uds-icurl">URL</label>
                <input id="uds-icurl" className="uds-inp mono" value={ic.rest_api.url || ''} placeholder="https://" disabled={!can('item_change')}
                  onChange={e => setItemChange({ mode: 'rest_api', rest_api: { ...ic.rest_api, url: e.target.value } } as ItemChange)} />
                <div className="uds-lbl">Method</div>
                <Seg value={ic.rest_api.method || 'POST'} disabled={!can('item_change')} ariaLabel="Method"
                  onChange={m => setItemChange({ mode: 'rest_api', rest_api: { ...ic.rest_api, method: m as 'GET' | 'POST' } } as ItemChange)}
                  options={[{ value: 'POST', label: 'POST' }, { value: 'GET', label: 'GET' }]} />
              </>
            )}
          </div>
        </div>
      </>
    )
  } else {
    body = (
      <>
        <div className="uds-hint" style={{ margin: '0 0 8px' }}>Validations on this column. Form-wide checks are under the form&apos;s Validations.</div>
        {validations.map((v, i) => {
          const rule = isValidationRule(v) ? v : null
          return (
            <div key={i} className={`uds-li ${rule?.disabled ? 'off' : ''}`}>
              <span className="t">{rule?.name || 'Validation'} · <span style={{ color: 'var(--color-text-faint)' }}>{validationSummary(v)}{rule?.severity === 'W' ? ' · Warning' : ''}</span></span>
              {rule && (
                <button type="button" className="uds-icbtn" disabled={!can('validations')}
                  title={rule.disabled ? 'Enable validation' : 'Disable validation (skip at save)'}
                  style={{ color: rule.disabled ? 'var(--color-text-faint)' : 'var(--color-accent)' }}
                  onClick={() => edit(col => { const list = [...(col.validations || [])] as Validation[]; const x = list[i]; if (isValidationRule(x)) list[i] = { ...x, disabled: !x.disabled }; col.validations = list as UdaColumn['validations'] })}>
                  <Power size={15} />
                </button>
              )}
              <button type="button" className="uds-icbtn" aria-label="Edit" disabled={!can('validations')} onClick={() => setEditor({ validation: i })}><Pencil size={14} /></button>
              <button type="button" className="uds-icbtn" aria-label="Delete" disabled={!can('validations')}
                onClick={() => edit(col => { col.validations = ((col.validations || []) as Validation[]).filter((_, k) => k !== i) as UdaColumn['validations'] })}><Trash2 size={14} /></button>
            </div>
          )
        })}
        {!validations.length && <div className="uds-na">No validations.</div>}
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!can('validations')} onClick={() => setEditor({ validation: validations.length })}>
            <Plus size={14} /> Add validation
          </button>
        </div>
      </>
    )
  }

  const editingValidation = editor && typeof editor === 'object' ? editor.validation : null
  return (
    <>
      <div className="uds-props-h">
        <div className="row"><OriginPills standard={isStandard(c)} published={mode === 'tenant' ? !!pub : null} />{c.retired && <span className="uds-pill uds-p-std">Retired</span>}</div>
        <h2>{c.heading || c.name}</h2>
        <div className="sub">uda_{c.name} · {set.label || set.key}</div>
      </div>
      <div className="uds-ptabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`uds-ptab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>
            {label}{k === 'rules' && validations.length > 0 && <span className="c">{validations.length}</span>}
          </button>
        ))}
      </div>
      <div className="uds-pbody">
        {std && (
          <HintBox icon={<Lock size={15} />} style={{ margin: '0 0 10px' }}>
            Standard column from the application. {allowedText ? `You may change its ${allowedText}.` : 'Its properties are fixed.'}
          </HintBox>
        )}
        {body}
      </div>
      {confirmDialog}
      {editor === 'lookup' && (
        <LookupEditor open onClose={() => setEditor(null)} column={colDef}
          formColumns={[...ownNames, ...attrNames]} headerColumns={[...headerNames, ...headerAttrNames]}
          project={project} catalog={catalog} matchRules={false}
          onSave={(patch: LookupPatch) => edit(col => {
            for (const [k, v] of Object.entries(patch)) {
              if (v === undefined || v === '') delete (col as unknown as Record<string, unknown>)[k]
              else (col as unknown as Record<string, unknown>)[k] = v
            }
          })} />
      )}
      {editor === 'rule' && (
        <ItemChangeRuleEditor open onClose={() => setEditor(null)} value={ic?.mode === 'rule' ? ic as ItemChangeRule : null}
          triggerColumnName={`uda_${c.name}`} formColumnNames={fillTargets} headerColumnNames={[...headerNames, ...headerAttrNames]}
          project={project} catalog={catalog} onSave={rule => setItemChange(rule)} />
      )}
      {editor === 'display' && (
        <DisplayFormatEditor open onClose={() => setEditor(null)} column={colDef} formColumns={[...ownNames, ...attrNames]}
          onSave={d => edit(col => { if (d) col.display = d; else delete col.display })} />
      )}
      {editingValidation !== null && (
        <ValidationRuleEditor open onClose={() => setEditor(null)}
          value={validations[editingValidation] ?? ''} title={`Validation · ${c.heading || c.name}`}
          formColumnNames={[...ownNames, ...attrNames]} headerColumnNames={[...headerNames, ...headerAttrNames]}
          formList={forms.map(f => ({ form_no: f.formNo, title: f.title }))} project={project} catalog={catalog}
          onOpenCode={method => openDialog({ kind: 'code', fnName: method, title: c.heading || c.name })}
          onSave={v => edit(col => {
            const list = [...((col.validations || []) as Validation[])]
            list[editingValidation] = v
            col.validations = list as UdaColumn['validations']
          })} />
      )}
    </>
  )
}
